import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import { searchContent } from '@plone/volto/actions/search/search';
import {
  getBaseUrl,
  flattenToAppURL,
  isInternalURL,
} from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';

/**
 * AgsusMegaNav — navegação principal com megamenu auto a partir da árvore Plone.
 *
 * - Itens de topo: vêm do estado de navegação do Volto (igual ao Navigation core).
 * - Filhos (painel): uma busca @search depth=2 (Folder+Document+Link), agrupada
 *   por seção. Auto-mantém: a UCOM adiciona/move páginas e o menu acompanha.
 * - Link com endereço externo (ex.: Intranet, AVA), no topo ou no painel,
 *   aponta direto para o destino. A @navigation só traz o endereço do objeto;
 *   o destino vem da mesma busca (depth=2 inclui o 1º nível).
 * - Painel só nas seções que TÊM filhos de navegação. Abre por hover E foco
 *   (teclado), fecha no Esc/blur. Desktop-first.
 *
 * Reusa as classes .navigation/.item do nav atual pra herdar todo o estilo;
 * só adiciona o painel (.ag-meganav__panel).
 */

const SUBREQ = 'agsus-meganav-children';
// A raiz ('/') nunca abre painel: a busca de depth=2 traz as secoes de 1o
// nivel, que caem no grupo '/', e o "Inicio" viraria megamenu de si mesmo.
const DISABLED_SUBMENU_PATHS = new Set(['/', '/trabalhe-na-agsus']);
// Itens do topo em destaque (fundo amarelo, texto preto), cada um um Link na
// raiz e por último na barra: no portal, a Intranet; na intranet, o Portal
// AgSUS. A lista é a mesma nos dois sites; o item vem do conteúdo de cada um.
const DESTAQUE_PATHS = new Set(['/intranet', '/portal-agsus']);
const LABEL_OVERRIDES = {
  '/areas-de-atuacao': 'Atuação',
};
const CHEVRON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23ffffff' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E";

// O endpoint seamless pode devolver URLs publicas do Plone sem o segmento
// /++api++. Essas URLs nao sao reconhecidas por flattenToAppURL quando o
// frontend roda em localhost. A arvore @navigation representa conteudo do
// proprio portal, portanto convertemos URLs absolutas em caminhos do app.
const navigationPath = (value) => {
  if (!value) return '/';
  const flattened = flattenToAppURL(value);
  // A raiz do site: o @id dela e o proprio endereco da API, e o flatten
  // devolve ''. Sem isto, o new URL() abaixo tiraria so o host e deixaria o
  // id do site ('/Plone') no link.
  if (flattened === '') return '/';
  if (flattened?.startsWith('/')) return flattened;

  try {
    const parsed = new URL(value);
    return `${parsed.pathname || '/'}${parsed.search}${parsed.hash}`;
  } catch {
    return flattened || '/';
  }
};

const semBarraFinal = (value) => (value || '').replace(/\/+$/, '');
const origemDe = (value) => {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

// id do painel de uma seção, para o aria-controls do gatilho
const painelId = (url) =>
  `ag-meganav-painel${url.replace(/[^a-z0-9]+/gi, '-').replace(/-$/, '')}`;

const AgsusMegaNav = ({ pathname }) => {
  const dispatch = useDispatch();
  const lang = useSelector((s) => s.intl?.locale);
  const navItems = useSelector((s) => s.navigation?.items || []);
  const childrenItems = useSelector(
    (s) => s.search?.subrequests?.[SUBREQ]?.items || [],
  );

  const [openUrl, setOpenUrl] = React.useState(null);
  const rootRef = React.useRef(null);

  // carrega itens de topo (igual ao Navigation core) + mapa de filhos (1 request)
  React.useEffect(() => {
    const { settings } = config;
    dispatch(getNavigation(getBaseUrl(pathname), settings.navDepth));
  }, [dispatch, pathname]);

  React.useEffect(() => {
    dispatch(
      searchContent(
        '/',
        {
          'path.depth': 2,
          portal_type: ['Folder', 'Document', 'Link'],
          sort_on: 'getObjPositionInParent',
          b_size: 600,
          metadata_fields: ['exclude_from_nav', 'getRemoteUrl'],
        },
        SUBREQ,
      ),
    );
  }, [dispatch]);

  // agrupa filhos por seção de topo (pai = path sem o último segmento) e
  // guarda o destino dos Links externos. Apontar para o próprio objeto não
  // basta: o LinkView do Volto só redireciona visitante deslogado, e quem está
  // logado cairia na página intermediária do Link. Link interno segue pelo
  // objeto.
  const { childrenBySection, externalHref, caminhoDoDestino } =
    React.useMemo(() => {
      const map = {};
      const hrefs = {};
      const destinos = {};
      childrenItems.forEach((it) => {
        if (it.exclude_from_nav) return;
        const url = navigationPath(it['@id']);
        const parent = url.substring(0, url.lastIndexOf('/')) || '/';
        const remote = it['@type'] === 'Link' ? it.getRemoteUrl : null;
        const href = remote && !isInternalURL(remote) ? remote : null;
        if (href) {
          hrefs[url] = href;
          destinos[semBarraFinal(href)] = url;
        }
        (map[parent] = map[parent] || []).push({ title: it.title, url, href });
      });
      return {
        childrenBySection: map,
        externalHref: hrefs,
        caminhoDoDestino: destinos,
      };
    }, [childrenItems]);

  // fecha no Esc
  React.useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpenUrl(null);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const renderLabel = (item, normalizedUrl) => {
    const isRoot = normalizedUrl === '/';
    if (isRoot) return 'Início';
    return LABEL_OVERRIDES[normalizedUrl] || item.title;
  };

  const itemUrl = (item) => navigationPath(item.url || item['@id']);
  // Sem login, a @navigation entrega um Link do topo pelo DESTINO
  // (ex.: https://intranet...), não pelo endereço do objeto. Reduzido ao
  // caminho, ele virava "/" e saía como um segundo "Início". É externo o
  // endereço que é destino de um Link da busca dos painéis (e aí o caminho do
  // objeto volta, com o destaque) ou que tem origem diferente da do site.
  const origemDoSite = origemDe(navItems[0]?.['@id']);
  const destinoExterno = (raw) => {
    if (!raw || !/^https?:\/\//i.test(raw)) return null;
    if (caminhoDoDestino[semBarraFinal(raw)]) return raw;
    if (isInternalURL(raw)) return null;
    const origem = origemDe(raw);
    return origem && origem !== origemDoSite ? raw : null;
  };
  const isMobileViewport = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 900px)').matches;

  return (
    <nav
      id="ag-mobile-menu"
      className="navigation ag-meganav"
      aria-label="Navegação principal"
      ref={rootRef}
      onMouseLeave={() => setOpenUrl(null)}
    >
      {/* Navegação do site com painéis que abrem e fecham (padrão "disclosure"),
          e não um menu de aplicativo: sem role menubar/menu/menuitem, que o
          leitor de tela anuncia como menu e exigem navegação por setas. */}
      <div className="ui secondary pointing menu">
        {navItems.map((item) => {
          const raw = item.url || item['@id'];
          const destino = destinoExterno(raw);
          const normalizedUrl = destino
            ? caminhoDoDestino[semBarraFinal(raw)] || raw
            : itemUrl(item);
          const isInternal = normalizedUrl.startsWith('/');
          const kids = DISABLED_SUBMENU_PATHS.has(normalizedUrl)
            ? []
            : childrenBySection[normalizedUrl] || [];
          const hasMenu = kids.length > 0;
          const isOpen = hasMenu && openUrl === normalizedUrl;

          if (!hasMenu) {
            const itemClass = DESTAQUE_PATHS.has(normalizedUrl)
              ? 'item ag-meganav__destaque'
              : 'item';
            const href = destino || externalHref[normalizedUrl];
            if (href) {
              return (
                <a key={normalizedUrl} href={href} className={itemClass}>
                  {renderLabel(item, normalizedUrl)}
                </a>
              );
            }
            return isInternal ? (
              <NavLink
                to={normalizedUrl}
                key={normalizedUrl}
                className={itemClass}
                activeClassName="active"
                exact={normalizedUrl === '/'}
              >
                {renderLabel(item, normalizedUrl)}
              </NavLink>
            ) : (
              <a key={item.url} href={item.url} className={itemClass}>
                {renderLabel(item, normalizedUrl)}
              </a>
            );
          }

          // item com megamenu
          return (
            <div
              key={normalizedUrl}
              className={`ag-meganav__group${isOpen ? ' is-open' : ''}`}
              // No celular só o toque (onClick) abre e fecha. Um toque dispara
              // também "mouse entrou" e foco, que abriam o painel antes do
              // clique; o clique, que alterna, via o painel aberto e fechava.
              onMouseEnter={() => {
                if (!isMobileViewport()) setOpenUrl(normalizedUrl);
              }}
              onFocus={() => {
                if (!isMobileViewport()) setOpenUrl(normalizedUrl);
              }}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget))
                  setOpenUrl(null);
              }}
            >
              <NavLink
                to={normalizedUrl}
                className="item ag-meganav__trigger"
                activeClassName="active"
                exact={normalizedUrl === '/'}
                aria-expanded={isOpen}
                aria-controls={painelId(normalizedUrl)}
                onClick={(e) => {
                  if (!isMobileViewport()) return;
                  e.preventDefault();
                  setOpenUrl(isOpen ? null : normalizedUrl);
                }}
              >
                {renderLabel(item, normalizedUrl)}
                <img
                  className="ag-meganav__chevron"
                  src={CHEVRON}
                  alt=""
                  aria-hidden="true"
                />
              </NavLink>

              <div className="ag-meganav__panel" id={painelId(normalizedUrl)}>
                <div className="ag-meganav__panel-inner">
                  <ul className="ag-meganav__list">
                    {kids.map((k) => (
                      <li key={k.url}>
                        {k.href ? (
                          <a href={k.href} className="ag-meganav__link">
                            {k.title}
                          </a>
                        ) : (
                          <NavLink to={k.url} className="ag-meganav__link">
                            {k.title}
                          </NavLink>
                        )}
                      </li>
                    ))}
                  </ul>
                  <NavLink
                    to={normalizedUrl}
                    className="ag-meganav__all"
                    exact={normalizedUrl === '/'}
                  >
                    Ver tudo em {item.title}
                    <span aria-hidden="true"> →</span>
                  </NavLink>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
};

AgsusMegaNav.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default AgsusMegaNav;
